import { Box, CircularProgress, FormHelperText, Stack, Typography } from '@mui/material';
import { SupportPolicy } from '@provision/entity-types';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { FullField, RuleGroupType } from 'react-querybuilder';
import { uniqBy } from 'remeda';
import { ConditionsBuilder } from 'src/components/ConditionsBuilder';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { FormRichTextBubble } from 'src/components/Forms/FormRichTextBubble/form-rich-text-bubble';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { useSelectedPartner, useUserGroupOptions } from 'src/graphql';
import { useSearchAddresses } from 'src/graphql/entities/search/search-address';
import { useSupportPolicyConditionFields } from 'src/graphql/entities/support-policy/hooks/use-support-policy-condition-fields';
import {
  createSupportPolicyDefaultRule,
  deserializeSupportPolicyConditions,
  mapSupportPolicyRuleValues,
  SUPPORT_POLICY_CONDITION_FIELDS,
  SupportPolicyRule,
  SupportPolicyRuleGroup,
  useSupportPolicyConditionOptions,
} from 'src/graphql/entities/support-policy/support-policy.conditions';
import {
  isValuelessConditionOperator,
  SUPPORT_POLICY_CONDITION_OPERATORS,
} from 'src/graphql/entities/support-policy/support-policy.operators';
import { getTimeConditionDefaultValue } from 'src/graphql/entities/support-policy/support-policy.time-fields';
import {
  NormalizedSupportPolicy,
  normalizeSupportPolicyScopeFields,
} from 'src/graphql/entities/support-policy/support-policy.utils';
import { getConditionDefaultValue } from 'src/pages/Settings/rules/condition-default-value';
import { muiComponents } from 'src/pages/Settings/rules/rules-form-config';
import {
  QueryOptionsValue,
  SupportedActionQueries,
} from 'src/pages/Settings/rules/use-rules-engine';
import { getValidConditionsCount } from 'src/utils/query-builder-conditions';

import { PolicyConditionValueEditor } from './policy-condition-value-editor';
import {
  POLICY_CATEGORIES_STANDARD,
  POLICY_TYPE_OPTIONS,
  TYPE_FRIENDLY_NAME_POLICIES_MAP,
} from './policy-constants';

const normalizeRuleValuesToStrings = (value: SupportPolicyRule['value']): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return (
            (item as { value?: unknown }).value ??
            (item as { id?: unknown }).id ??
            (item as { label?: unknown }).label ??
            null
          );
        }
        return item;
      })
      .filter((val): val is string | number | boolean => val !== null && val !== undefined)
      .map((val) => String(val));
  }

  if (typeof value === 'object' && value !== null) {
    const nested =
      (value as { value?: unknown }).value ??
      (value as { id?: unknown }).id ??
      (value as { label?: unknown }).label;
    return nested === undefined || nested === null ? [] : [String(nested)];
  }

  return value === undefined || value === null ? [] : [String(value)];
};

const getRuleAttribute = (rule: SupportPolicyRule): string => {
  const field = rule.field as { attribute?: unknown; value?: unknown; uiType?: unknown };

  return typeof field === 'string'
    ? field
    : ((field?.attribute as string) ?? field?.value ?? '');
};

const collectValuesForAttribute = (
  group: SupportPolicyRuleGroup,
  attribute: string,
): string[] => {
  const results: string[] = [];

  const walk = (current?: SupportPolicyRuleGroup) => {
    current?.rules?.forEach((rule) => {
      if ('rules' in rule) {
        walk(rule as SupportPolicyRuleGroup);
        return;
      }

      if (getRuleAttribute(rule) !== attribute) return;

      normalizeRuleValuesToStrings(rule.value).forEach((value) => results.push(value));
    });
  };

  walk(group);

  return results;
};

export interface SupportPolicyFormValues {
  title: string;
  description: string;
  type: string;
  category: string;
  validUntil?: Dayjs | null;
  pinnedAt: boolean;
  userGroups?: string[];
  addressIds: string[];
  clientIds: string[];
  clientToAddressIds: string[];
  enableConditions: boolean;
  conditions: SupportPolicyRuleGroup;
  urgencyLevel?: string | null;
  escalationRules?: string | null;
}

type SupportPolicyFormModel = Omit<SupportPolicyFormValues, 'type' | 'category'> & {
  type: string | null;
  category: string | null;
};

interface SupportPolicyFormProps {
  policy?: Partial<SupportPolicy> | Partial<NormalizedSupportPolicy>;
  onSave: (values: SupportPolicyFormValues) => void;
  onClose: () => void;
}

const DESCRIPTION_REQUIRED_MESSAGE = 'Description is required';
const CONDITIONS_REQUIRED_MESSAGE = 'Add at least one condition to save this policy';

const getTypeLabel = (type: string): string => TYPE_FRIENDLY_NAME_POLICIES_MAP[type] ?? type;

export const PolicyUserGroupsField = ({ testId }: { testId: string }) => {
  const { userGroupOptions } = useUserGroupOptions();
  const groupIds = useMemo(
    () => userGroupOptions.map((group) => group.id),
    [userGroupOptions],
  );

  return (
    <FormAutocomplete<string>
      type="autocomplete"
      data-testid={testId}
      name="userGroups"
      label="Available To"
      multiple
      options={groupIds}
      getOptionLabel={(id) => userGroupOptions.find((group) => group.id === id)?.label ?? id}
    />
  );
};

export const SupportPolicyForm = ({ policy, onSave, onClose }: SupportPolicyFormProps) => {
  const {
    fields: conditionFieldDefinitions,
    fieldMap: conditionFieldMap,
    isLoading: isLoadingConditionFields,
  } = useSupportPolicyConditionFields();

  const defaults = useMemo<SupportPolicyFormModel>(() => {
    const scope = normalizeSupportPolicyScopeFields(policy);

    return {
      title: policy?.title ?? '',
      description: policy?.description ?? '',
      type: policy?.type ?? null,
      category: policy?.category ?? null,
      validUntil: policy?.validUntil ? dayjs(policy.validUntil) : null,
      pinnedAt: Boolean(policy?.pinnedAt),
      userGroups: policy?.userGroups?.map((g) => g.id) ?? [],
      addressIds: scope.addressIds,
      clientIds: scope.clientIds,
      clientToAddressIds: scope.clientToAddressIds,
      enableConditions: Boolean(policy?.conditions),
      conditions: deserializeSupportPolicyConditions(policy?.conditions, conditionFieldMap),
      urgencyLevel: policy?.urgencyLevel ?? null,
      escalationRules: policy?.escalationRules ?? null,
    };
  }, [policy, conditionFieldMap]);

  const formMethods = useForm<SupportPolicyFormModel>({
    defaultValues: defaults,
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid },
  } = formMethods;

  const { field: conditionsField } = useController({
    control,
    name: 'conditions',
    rules: {
      validate: (value, formValues) =>
        !formValues.enableConditions ||
        getValidConditionsCount(value, isValuelessConditionOperator) > 0 ||
        CONDITIONS_REQUIRED_MESSAGE,
    },
  });

  const { selectedPartner } = useSelectedPartner();
  const conditionsEnabled = watch('enableConditions');
  const watchedConditions = watch('conditions');
  const baseConditionQueryOptions = useSupportPolicyConditionOptions();
  const addressIdsFromConditions = useMemo(
    () => uniqBy(collectValuesForAttribute(watchedConditions, 'addressId'), (id) => id),
    [watchedConditions],
  );

  const { records: addressLookupRecords, isLoading: isAddressLookupLoading } =
    useSearchAddresses({
      searchTerm: null,
      filter: addressIdsFromConditions.length
        ? [
            {
              attribute: 'addressId',
              operator: 'in',
              value: addressIdsFromConditions.join(','),
            },
          ]
        : [],
      limit: addressIdsFromConditions.length || 10,
      showResultsWhenEmptySearchTerm: true,
      skip: addressIdsFromConditions.length === 0,
    });

  const addressLookupOptions = useMemo(
    () =>
      addressLookupRecords?.map((address) => ({
        id: address.id ?? address.addressId,
        value: address.id ?? address.addressId,
        label: address.name ?? address.fullAddress ?? address.address1 ?? address.addressId,
      })) ?? [],
    [addressLookupRecords],
  );

  const conditionQueryOptions = useMemo(() => {
    const existingAddressOptions: QueryOptionsValue = baseConditionQueryOptions[
      SupportedActionQueries.getAddress
    ] ?? {
      options: [],
    };

    const mergedAddressOptions = uniqBy(
      [
        ...(existingAddressOptions.historicalOptions ?? []),
        ...(existingAddressOptions.options ?? []),
        ...addressLookupOptions,
      ],
      (opt) =>
        String(
          (opt as { value?: unknown; id?: unknown }).value ??
            (opt as { value?: unknown; id?: unknown }).id ??
            '',
        ),
    );

    return {
      ...baseConditionQueryOptions,
      [SupportedActionQueries.getAddress]: {
        ...existingAddressOptions,
        options: mergedAddressOptions,
        historicalOptions: mergedAddressOptions,
        isLoading: existingAddressOptions?.isLoading || isAddressLookupLoading,
      },
    };
  }, [addressLookupOptions, baseConditionQueryOptions, isAddressLookupLoading]);
  const queryBuilderContext = useMemo(
    () => ({
      timezone: selectedPartner?.timeZone ?? 'America/New_York',
      queryOptions: conditionQueryOptions,
    }),
    [conditionQueryOptions, selectedPartner?.timeZone],
  );

  const conditionFields = useMemo(
    () =>
      conditionFieldDefinitions.map((field) => {
        if (!field.optionsQuery) {
          return field;
        }

        if (!(field.optionsQuery in conditionQueryOptions)) {
          return field;
        }

        const optionsData =
          conditionQueryOptions[field.optionsQuery as SupportedActionQueries];
        if (!optionsData) {
          return field;
        }

        const options = uniqBy(
          [...(optionsData.historicalOptions ?? []), ...(optionsData.options ?? [])],
          (opt) =>
            String(
              (opt as { id?: string | number | boolean }).id ??
                (opt as { value?: string | number | boolean }).value ??
                (opt as { label?: string }).label ??
                '',
            ),
        );

        return {
          ...field,
          options,
          fieldOptions: options,
        };
      }),
    [conditionFieldDefinitions, conditionQueryOptions],
  );

  const mappedConditions = useMemo(
    () => mapSupportPolicyRuleValues(watchedConditions, conditionQueryOptions),
    [conditionQueryOptions, watchedConditions],
  );

  useEffect(() => {
    if (!conditionsEnabled) return;

    const hasRules = Boolean(watchedConditions?.rules?.length);

    if (!hasRules) {
      setValue(
        'conditions',
        {
          combinator: 'and',
          rules: [createSupportPolicyDefaultRule()],
        },
        { shouldValidate: true },
      );
    }
  }, [conditionsEnabled, setValue, watchedConditions]);

  const validConditionsCount = useMemo(
    () => getValidConditionsCount(watchedConditions, isValuelessConditionOperator),
    [watchedConditions],
  );

  useEffect(() => {
    reset(defaults, { keepDirtyValues: true });
  }, [defaults, reset]);

  const submitHandler = (data: SupportPolicyFormModel) => {
    onSave({ ...data, type: data.type ?? '', category: data.category ?? '' });
  };

  const isEditing = Boolean(policy?.id);

  return (
    <ModalContainer
      open
      size="large"
      title={isEditing ? 'Edit policy' : 'Add policy'}
      data-testid="support-policy-modal"
      onClose={onClose}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'support-policy-modal-cancel' },
        {
          label: isEditing ? 'Save policy' : 'Add policy',
          kind: 'primary',
          onClick: handleSubmit(submitHandler),
          disabled: isLoadingConditionFields || !isValid,
          testId: 'support-policy-modal-save',
        },
      ]}
    >
      {isLoadingConditionFields ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <GenericForm<SupportPolicyFormModel>
          formMethods={formMethods}
          data-testid="support-policy-form"
        >
          <FormTextField
            type="textfield"
            data-testid="support-policy-title"
            name="title"
            label="Title"
            required="Title is required"
            showErrorText
          />

          <FormRichTextBubble
            type="rich-text-bubble"
            data-testid="support-policy-description"
            name="description"
            label="Description"
            required={DESCRIPTION_REQUIRED_MESSAGE}
            rules={{
              validate: (value: string) =>
                value?.replace(/<[^>]*>/g, '').trim() !== '' || DESCRIPTION_REQUIRED_MESSAGE,
            }}
          />

          <Stack direction="row" gap={1.5}>
            <FormAutocomplete<string>
              type="autocomplete"
              data-testid="support-policy-type"
              name="type"
              label="Type"
              required="Type is required"
              options={POLICY_TYPE_OPTIONS}
              getOptionLabel={getTypeLabel}
              sx={{ flex: 1 }}
            />
            <FormAutocomplete<string>
              type="autocomplete"
              data-testid="support-policy-category"
              name="category"
              label="Category"
              required="Category is required"
              options={[...POLICY_CATEGORIES_STANDARD]}
              sx={{ flex: 1 }}
            />
          </Stack>

          <PolicyUserGroupsField testId="support-policy-user-groups" />

          <Stack gap={1}>
            <Typography variant="subtitle2">Filters</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <FormSwitch
                type="switch"
                data-testid="support-policy-filter-by-conditions"
                name="enableConditions"
                label="Filter by conditions"
                switchFieldProps={{ inputProps: { 'aria-label': 'Filter by conditions' } }}
                rules={{ deps: 'conditions' }}
              />
              {conditionsEnabled ? (
                <Typography variant="caption" color="text.secondary">
                  {validConditionsCount
                    ? `${validConditionsCount} condition${validConditionsCount === 1 ? '' : 's'}`
                    : 'No conditions yet'}
                </Typography>
              ) : null}
            </Stack>

            {conditionsEnabled ? (
              <Stack gap={1}>
                <QueryBuilderMaterial muiComponents={muiComponents}>
                  <ConditionsBuilder
                    data-testid="support-policy-conditions"
                    enableDragAndDrop
                    getDefaultField={(items) =>
                      (conditionFields[0] ??
                        items?.[0] ??
                        SUPPORT_POLICY_CONDITION_FIELDS[0]) as unknown as string
                    }
                    enableMountQueryChange={false}
                    autoSelectField
                    resetOnFieldChange
                    resetOnOperatorChange={false}
                    context={queryBuilderContext}
                    getOperators={(field) =>
                      // @ts-expect-error lib incorrectly types this prop
                      (field as (typeof SUPPORT_POLICY_CONDITION_FIELDS)[number])?.operators ??
                      SUPPORT_POLICY_CONDITION_OPERATORS
                    }
                    query={mappedConditions as unknown as RuleGroupType}
                    onQueryChange={(value: unknown) =>
                      conditionsField.onChange(value as SupportPolicyRuleGroup)
                    }
                    fields={conditionFields as unknown as FullField[]}
                    getDefaultRule={() => ({
                      ...createSupportPolicyDefaultRule(),
                      field: conditionFields[0] ?? SUPPORT_POLICY_CONDITION_FIELDS[0],
                    })}
                    // a freshly picked Hours condition is complete right away (Open / weekdays
                    // 09:00–17:00); every other attribute takes the rules engine's default
                    getDefaultValue={(rule) =>
                      getTimeConditionDefaultValue(
                        getRuleAttribute(rule as unknown as SupportPolicyRule),
                      ) ?? getConditionDefaultValue(rule)
                    }
                    // @ts-expect-error The editor reads the policy field shape, wider than FullField.
                    valueEditor={PolicyConditionValueEditor}
                  />
                </QueryBuilderMaterial>
                {errors.conditions?.message ? (
                  <FormHelperText error data-testid="support-policy-conditions-error">
                    {errors.conditions.message}
                  </FormHelperText>
                ) : null}
              </Stack>
            ) : null}
          </Stack>

          <FormDatePicker
            type="datepicker"
            data-testid="support-policy-valid-until"
            name="validUntil"
            label="Valid Until"
            fullWidth
            datePickerProps={{ minDate: dayjs().add(1, 'day') }}
          />

          <FormSwitch
            type="switch"
            data-testid="support-policy-pin"
            name="pinnedAt"
            label="Pin this policy"
            switchFieldProps={{ inputProps: { 'aria-label': 'Pin this policy' } }}
          />
        </GenericForm>
      )}
    </ModalContainer>
  );
};
